import { Pause, Play, RotateCcw } from 'lucide-react';
import { useRef, useState } from 'react';
import { TAPE_DESK } from '../../data/theTape';
import { useReducedMotion, useVideoKeepAlive } from '../../motion';

const VIDEO_BASE = '/videos/tape/coronation-cold-open';

export default function TapeColdOpen() {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [ended, setEnded] = useState(false);
  const [manualPause, setManualPause] = useState(false);
  const reduced = useReducedMotion();
  useVideoKeepAlive(videoRef, {
    enabled: !reduced && !manualPause && !ended,
    onPlayRejected: () => setPlaying(false),
  });

  const togglePlayback = () => {
    const video = videoRef.current;
    if (!video) return;

    if (ended) {
      video.currentTime = 0;
      setEnded(false);
      setManualPause(false);
      video.play().catch(() => setPlaying(false));
      return;
    }

    if (video.paused) {
      setManualPause(false);
      video.play().catch(() => setPlaying(false));
    } else {
      setManualPause(true);
      video.pause();
    }
  };

  const buttonLabel = ended ? 'Replay the cold open' : playing ? 'Pause the cold open' : 'Play the cold open';
  const ButtonIcon = ended ? RotateCcw : playing ? Pause : Play;

  return (
    <section
      className="relative overflow-hidden border-y border-accent-red/30 bg-black px-6 py-10 md:px-14 md:py-14"
      aria-labelledby="tape-cold-open-title"
      data-testid="tape-cold-open"
    >
      <div className="pointer-events-none absolute inset-0 opacity-20"
           style={{ backgroundImage: 'radial-gradient(circle at 72% 28%, rgba(220,38,38,.45), transparent 35%), linear-gradient(rgba(255,255,255,.035) 1px, transparent 1px)', backgroundSize: 'auto, 100% 4px' }}
           aria-hidden="true" />

      <div className="relative mx-auto grid max-w-6xl items-center gap-8 lg:grid-cols-[minmax(250px,0.72fr)_minmax(0,1.28fr)] lg:gap-12">
        <div>
          <div className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-accent-red">
            The Tape · The Coronation
          </div>
          <h2 id="tape-cold-open-title" className="mt-3 font-display text-4xl font-black uppercase leading-[0.9] md:text-5xl">
            He ranked them all.
            <span className="block text-accent-gold">Himself included.</span>
          </h2>
          <p className="mt-5 max-w-[36ch] font-body text-base italic leading-relaxed text-ink-secondary md:text-lg">
            Ten videos. Ten rankings. The last name on the board belongs to the man holding the camera — 50.01%, a mandate with one vote cast. AJ reviewed the decision. He would have done the same.
          </p>
          <div className="mt-6 inline-flex border border-accent-red/50 bg-accent-red/10 px-3 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-accent-red">
            Current board · {TAPE_DESK.publishedCount} of 10 published
          </div>
        </div>

        <div className="relative aspect-video overflow-hidden border border-white/15 bg-bg-deep shadow-[0_24px_70px_rgba(0,0,0,0.55)]">
          <video
            ref={videoRef}
            className="h-full w-full object-cover"
            muted
            playsInline
            preload="auto"
            poster={`${VIDEO_BASE}-poster.webp`}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onEnded={() => { setPlaying(false); setEnded(true); }}
            data-testid="tape-cold-open-video"
          >
            <source src={`${VIDEO_BASE}-480p.webm`} type="video/webm" />
            <source src={`${VIDEO_BASE}-480p.mp4`} type="video/mp4" />
          </video>

          <div className="pointer-events-none absolute inset-x-0 top-0 flex items-center justify-between bg-gradient-to-b from-black/75 to-transparent px-3 pb-7 pt-3 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-white/75" aria-hidden="true">
            <span>Bruins 09 · The Tape</span>
            <span>00:10.0</span>
          </div>

          <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 bg-gradient-to-t from-black/85 via-black/35 to-transparent px-3 pb-3 pt-12" aria-hidden="true">
            <div className="hidden font-display text-[11px] font-black uppercase tracking-[0.16em] text-white sm:block">
              Teichmann v. Krow
            </div>
            <div className="hidden font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-accent-gold sm:block">
              {TAPE_DESK.boardComplete ? 'The board is complete' : 'Argument in progress'}
            </div>
          </div>

          {ended && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/62 px-6 text-center">
              <div>
                <div className="font-mono text-[9px] font-bold uppercase tracking-[0.24em] text-accent-red">
                  Review complete
                </div>
                <div className="mt-2 font-display text-2xl font-black uppercase leading-none text-white md:text-3xl">
                  He ranked himself first. The ranking stands.
                </div>
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={togglePlayback}
            className="absolute bottom-3 left-1/2 z-10 inline-flex min-h-11 -translate-x-1/2 items-center gap-2 border border-white/40 bg-black/85 px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-white transition-colors hover:border-accent-gold hover:text-accent-gold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-gold md:bottom-4"
            aria-label={buttonLabel}
          >
            <ButtonIcon size={14} aria-hidden="true" />
            {ended ? 'Run it back' : playing ? 'Hold tape' : 'Roll tape'}
          </button>
        </div>
      </div>
    </section>
  );
}
